import React, {ReactNode} from 'react';
import {TimeSpentReminderBox} from "./boxes/time-spent-reminder/time-spent-reminder.box";
import {Check} from "@sparkware/app-utils";
import {OrbitComponentWithState} from "../base-components/orbit-component-with-state/orbit-component-with-state";
import {Button} from "../../basic-ui-components/button/base/button.component";
import {TimeSpentReminderOptionSelectorBox} from "./boxes/option-selector/option-selector.box";
import {TimeSpentReminderMainTextBox} from "./boxes/main-text/main-text.box";
import {TimeSpentReminderSelectorContainerBox} from "./boxes/selector-container/selector-container.box";
import {IModalDialogHandler} from "../../services/dialog/dialog.service.interface";
import {DISPLAY_MESSAGE_IDS} from "../../../common/const/casinofied-analytics-consts";
import { TSR_TYPE, ITimeSpentReminderOptions } from '../../../common/interfaces/orbit-component-to-sdk-adapter/components-props/time-spent-reminder-options';

enum UK_TSR_OPTIONS {
    UK_TSR_RESUME_GAME_OPT = "UK_TSR_RESUME_GAME_OPT",
    UK_TSR_CONTINUE_OPT = "UK_TSR_CONTINUE_OPT",
    UK_TSR_STOP_REMINDERS_OPT = "UK_TSR_STOP_REMINDERS_OPT",
    UK_TSR_REVIEW_REMINDERS_OPT = "UK_TSR_REVIEW_REMINDERS_OPT",
    UK_TSR_HISTORY_OPT = "UK_TSR_HISTORY_OPT",
    UK_TSR_CLOSE_GAME_OPT = "UK_TSR_CLOSE_GAME_OPT",
    UK_TSR_LOGOUT_OPT = "UK_TSR_LOGOUT_OPT"
}

const ELASTIC_CATEGORY: string = "Client_RealityCheck_Detail";

const TRACKING_UK_TSR_OPTION_NAMES: Record<UK_TSR_OPTIONS, string> = {
    [UK_TSR_OPTIONS.UK_TSR_RESUME_GAME_OPT]: "ResumeGame",
    [UK_TSR_OPTIONS.UK_TSR_CONTINUE_OPT]: "Continue",
    [UK_TSR_OPTIONS.UK_TSR_STOP_REMINDERS_OPT]: "StopReminders",
    [UK_TSR_OPTIONS.UK_TSR_REVIEW_REMINDERS_OPT]: "ReviewReminders",
    [UK_TSR_OPTIONS.UK_TSR_HISTORY_OPT]: "ViewHistory",
    [UK_TSR_OPTIONS.UK_TSR_CLOSE_GAME_OPT]: "CloseGame",
    [UK_TSR_OPTIONS.UK_TSR_LOGOUT_OPT]: "LogOut"
};

const TSR_OPTIONS_BY_TYPE: Record<TSR_TYPE, UK_TSR_OPTIONS[]> = {
    [TSR_TYPE.SESSION_TIMER]: [
        UK_TSR_OPTIONS.UK_TSR_CONTINUE_OPT,
        UK_TSR_OPTIONS.UK_TSR_REVIEW_REMINDERS_OPT,
        UK_TSR_OPTIONS.UK_TSR_HISTORY_OPT,
        UK_TSR_OPTIONS.UK_TSR_LOGOUT_OPT
    ],
    [TSR_TYPE.GAME_TIMER]: [
        UK_TSR_OPTIONS.UK_TSR_RESUME_GAME_OPT,
        UK_TSR_OPTIONS.UK_TSR_STOP_REMINDERS_OPT,
        UK_TSR_OPTIONS.UK_TSR_HISTORY_OPT,
        UK_TSR_OPTIONS.UK_TSR_CLOSE_GAME_OPT
    ]
}

export type ITimeSpentReminderComponentProps = ITimeSpentReminderOptions & TimeSpentReminderComponentBaseProps;
interface TimeSpentReminderComponentBaseProps {
    dialogHandler: IModalDialogHandler;
};


interface TimeSpentReminderComponentState {
    selectedOption: UK_TSR_OPTIONS;
}

export class TimeSpentReminderComponent extends OrbitComponentWithState<ITimeSpentReminderComponentProps,TimeSpentReminderComponentState> {

    private _businessCorrelationId: string;

    constructor(props: ITimeSpentReminderComponentProps) {
        super(props);

        this.state = {
            selectedOption: UK_TSR_OPTIONS.UK_TSR_RESUME_GAME_OPT
        };
    }

    componentDidMount() {
        super.componentDidMount();
        this._businessCorrelationId = this.services.tools.getGuid();
        this.services.casinofiedAnalytics.messageDisplay({
            businessCorrelationID: this._businessCorrelationId,
            displayMessageID: DISPLAY_MESSAGE_IDS.timeSpentReminder,
            displayMessageName: 'TimeSpentReminder'
        });
    }

    private _renderLoggedInText = (): ReactNode => {
        const playingTimeWithoutUnits = this.props.playingTime.toHoursAndMinutesString();
        //Temporary hack to also display 'h' and 'm', should probably modify TimeSpan class to support this
        const playingTime = `${playingTimeWithoutUnits.substring(0, 2)}h${playingTimeWithoutUnits.substring(2)}m`;
        return (
            <TimeSpentReminderMainTextBox className={"cy-uk-tsr-main-text-box"}>
                <span>{this.services.translation.translate('TSR_MAIN_TEXT')}</span>
                <span>{playingTime}</span>
            </TimeSpentReminderMainTextBox>
        );
    };

    private _renderSelectorContainer = (): ReactNode => {
        return (
            <TimeSpentReminderSelectorContainerBox className={'cy-reality-check-options'}>
                <span>{this.services.translation.translate('TSR_SUBTEXT')}</span>
                {this._renderSelect()}
                <Button
                    onClick={this._executeAction}
                    preset="primary"
                    fullWidth={true}
                >
                    {this.services.translation.translate('TSR_CONFIRM')}
                </Button>
            </TimeSpentReminderSelectorContainerBox>
        );
    };

    private _renderSelect = (): ReactNode => {
        return (
            <TimeSpentReminderOptionSelectorBox
                className={'cy-drop-down-box'}
                value={this.state.selectedOption}
                onChange={e => this._updateSelection(e.target.value as UK_TSR_OPTIONS)}>
                {Object.values(TSR_OPTIONS_BY_TYPE[this.props.timerType]).map(tsrOption =>
                    <option value={tsrOption}
                            key={tsrOption}>
                        {this.services.translation.translate(tsrOption)}
                    </option>
                )}
            </TimeSpentReminderOptionSelectorBox>
        );
    };

    private _updateSelection = (selection: UK_TSR_OPTIONS): void => {
        return this.setState({
            selectedOption: selection
        });
    };

    private _executeAction = (): void => {
        this._sendActionDataTracking();
        switch (this.state.selectedOption) {
            case UK_TSR_OPTIONS.UK_TSR_RESUME_GAME_OPT:
            case UK_TSR_OPTIONS.UK_TSR_CONTINUE_OPT:
                this._closeDialogBox();
                this.services.runningGame.continuePlayingCurrentGame();
                break;
            case UK_TSR_OPTIONS.UK_TSR_HISTORY_OPT:
                this._closeDialogBox();
                this.services.gameRouteHandler.redirectToHistory();
                break;
            case UK_TSR_OPTIONS.UK_TSR_CLOSE_GAME_OPT:
                this._closeDialogBox();
                this.services.runningGame.closeRunningGame();
                break;
            case UK_TSR_OPTIONS.UK_TSR_LOGOUT_OPT:
                this._closeDialogBox();
                this.services.runningGame.closeRunningGame();
                this.services.user.logout();
                break;
            case UK_TSR_OPTIONS.UK_TSR_REVIEW_REMINDERS_OPT:
                this._closeDialogBox();
                this.services.runningGame.closeRunningGame();
                //this.services.safezone.openFullSafezone();
                window['CGP'].openOptions('ReminderSettings');
                break;
            case UK_TSR_OPTIONS.UK_TSR_STOP_REMINDERS_OPT:
                this._onStopFurtherReminders();
                this._closeDialogBox();
                this.services.runningGame.continuePlayingCurrentGame();
                break;
        }
    };

    private _onStopFurtherReminders = (): void => {
        if (this.props.timerType === TSR_TYPE.GAME_TIMER) {
            if(Check.isFunction(this.props.onStopReminders)){
                this.props.onStopReminders();
            } else {
                console.error("Time Spent Reminder: invalid callback to stop further reminders");
            }
        }
    };

    private _closeDialogBox = (): void => {
        this.props.dialogHandler.close();
    };

    private _sendActionDataTracking = (): void => {
        const action = TRACKING_UK_TSR_OPTION_NAMES[this.state.selectedOption];

        this._sendActionDataToElastic(action);

        this.services.casinofiedAnalytics.messageActionTaken({
            actionType: action,
            businessCorrelationID: this._businessCorrelationId
        });
    };

    private _sendActionDataToElastic = (action: string): void => {
        const logger = this.services.analytics.getLogger({
          CGPEventCategory: ELASTIC_CATEGORY,
          CGPEventAction: action
        });

        logger.setTarget('casino-realityCheck');
        logger.info();
    };

    render = (): ReactNode => {
        const TimeSpentReminderIcon = this.services.theme.currentTheme.icons.timeSpentReminderClockIcon;
        return (
            <TimeSpentReminderBox>
                <TimeSpentReminderIcon style={{color: this.services.theme.currentTheme.colors.popupIconColor}} size={5.6}/>
                {this._renderLoggedInText()}
                {this._renderSelectorContainer()}
            </TimeSpentReminderBox>
        );
    }
}